import { memo, useMemo } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';
import { BatteryIcon } from '@/assets/battery-icon.tsx';
import { LocationIcon } from '@/assets/location-icon.tsx';
import { ThreeVerticalDotsIcon } from '@/assets/three-vertical-dots-icon.tsx';
import PlaceholderPlantImage from '@/assets/placeholder-plant.jpg';
import { ImageWithPlaceholder } from './image-with-placeholder.tsx';
import { useDeletePlantQuery } from '@/features/plants/hooks/mutations/[plantId]/use-delete-plant.ts';
import { useQueryClient } from '@tanstack/react-query';
import { PlantUserAccessManagementDialog } from '@/features/plants/components/plant-management/user-access/plant-user-access-management.tsx';
import { useSession } from '@/services/providers/use-session.ts';
import { usePermissions } from '@/hooks/use-permission.ts';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge.tsx';
import { PlantsWithOverview } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { SolarPanelArrayIcon } from '@/assets/solar-panel-array-icon.tsx';
import { getImageUrl } from '@/features/plants/utils/get-image-url.ts';

const formatValueAndUnit = (inputNumber: number | undefined) => {
  if (inputNumber == null) {
    return { value: '--', unit: '' };
  }

  const isValueBelow1k = inputNumber < 1000;
  const value = isValueBelow1k ? inputNumber.toFixed(2) : (inputNumber / 1000).toFixed(2);
  const unit = isValueBelow1k ? 'kWh' : 'MWh';

  return { value, unit };
};

function getBadgeVariant(
  type: 'plantStatus' | 'mqttStatus',
  value: string | number | undefined
) {
  if (type === 'plantStatus') {
    switch (value) {
      case 'Operational':
        return 'green';
      case 'Planning':
        return 'blue';
      case 'Under Construction':
        return 'yellow';
      default:
        return 'gray';
    }
  } else if (type === 'mqttStatus') {
    switch (Number(value)) {
      case 1:
        return 'green'; // connected / online
      case 0:
        return 'red'; // error / disconnected
      case -1:
        return 'gray'; // Unknown
      default:
        return 'gray'; // Unknown
    }
  }
  return 'gray'; // Default fallback
}

function getMqttStatusLabel(mqttStatus: number | undefined): string {
  switch (mqttStatus) {
    case 1:
      return 'Connected';
    case 0:
      return 'Disconnected';
    case -1:
      return 'Unknown';
    default:
      return 'Unknown';
  }
}

export const PlantCard = memo(function PlantCard({
  plant,
  viewType,
}: {
  plant: PlantsWithOverview;
  viewType?: 'production-only' | 'production-storage' | 'storage-only' | string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const deletePlantMutation = useDeletePlantQuery({ queryClient });
  const [
    canViewPlant,
    canViewControlPanel,
    canViewEdit,
    canViewConnectionDetails,
    canViewAccessManagement,
    canDelete,
  ] = usePermissions<'plants'>([
    ['plants', 'view-page.plant'],
    ['plants', 'view-page.control-panel'],
    ['plants', 'view-page.edit'],
    ['plants', 'view-page.connection-details'],
    ['plants', 'view-page.user-access-management'],
    ['plants', 'delete'],
  ]);

  const allowedUsers = ['catalin.petrovici@nokto.eu', 'alex.paval@nokto.eu'];
  const { email } = useSession();

  const metrics = useMemo(
    () =>
      viewType === undefined ||
      viewType === 'production-only' ||
      viewType === 'production-storage'
        ? [
            {
              label: t('metrics.today_production'),
              value: formatValueAndUnit(plant.yieldToday),
            },
            {
              label: t('metrics.ytd_production'),
              value: formatValueAndUnit(plant.totalYield),
            },
            {
              label: t('metrics.today_consumption'),
              value: formatValueAndUnit(plant.consumptionToday),
            },
            {
              label: t('metrics.today_fed_to_grid'),
              value: formatValueAndUnit(plant.fedToGrid),
            },
          ]
        : [
            {
              label: t('metrics.battery_state_of_charge'),
              value: {
                value: plant.batteryStateOfCharge ?? '--',
                unit: plant.batteryStateOfCharge != null ? '%' : '',
              },
            },
            {
              label: t('metrics.battery_status'),
              value: {
                value: plant.batteryStatus || '--',
                unit: undefined,
              },
            },
            {
              label: t('metrics.today_battery_charge'),
              value: formatValueAndUnit(plant.batteryChargePowerToday),
            },
            {
              label: t('metrics.today_battery_discharge'),
              value: formatValueAndUnit(plant.batteryDischargePowerToday),
            },
          ],
    [plant, viewType, t]
  );

  return (
    <div className="@container flex w-full min-w-[310px] overflow-hidden rounded-xl border border-[#E4E4E4] bg-white">
      <Link
        to={plant.plantId}
        className="w-[80px] min-w-[80px] @md:w-[120px] @md:min-w-[120px] @lg:w-[160px] @lg:min-w-[160px]"
      >
        <ImageWithPlaceholder
          src={getImageUrl(plant?.signedImageUrl)}
          alt={plant.basicDetails.name}
          loading="lazy"
          placeholderSrc={PlaceholderPlantImage}
          className="h-full w-full object-cover"
        />
      </Link>

      <div className="flex flex-1 flex-col gap-1 p-2 pl-3 @md:pl-4">
        <div className="flex justify-between">
          <div className="w-[175px] @sm:w-[220px] @md:w-[270px]">
            <h2 className="truncate text-lg font-semibold" title={plant.basicDetails.name}>
              <Link to={plant.plantId} className="hover:underline">
                {plant.basicDetails.name}
              </Link>
            </h2>

            <div className="flex gap-2 text-nowrap">
              <Badge variant={getBadgeVariant('plantStatus', plant.basicDetails.status)}>
                {plant.basicDetails.status}
              </Badge>
              <Badge variant={getBadgeVariant('mqttStatus', plant?.mqttStatus)}>
                {getMqttStatusLabel(plant?.mqttStatus)}
              </Badge>
            </div>

            <div className="mt-1 flex gap-2">
              <div className="flex max-w-full items-center gap-1">
                <LocationIcon width="14" height="14" />
                <span className="max-w-[150px] truncate text-xs font-medium text-[#7D7D85] @md:max-w-[200px] @md:text-sm @lg:max-w-[300px]">
                  {plant.siteInformation.city?.label ?? plant.siteInformation.country?.label}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[#7D7D85]">
                {Boolean(plant.technicalSpecifications.setupType === 'production-storage') && (
                  <SolarPanelArrayIcon width="20" height="17" />
                )}
                {Boolean(plant.technicalSpecifications.setupType === 'production-only') && (
                  <SolarPanelArrayIcon width="20" height="17" />
                )}
                {Boolean(plant.technicalSpecifications.setupType !== 'production-only') && (
                  <BatteryIcon width="16" />
                )}
              </div>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 shrink-0 p-0">
                <ThreeVerticalDotsIcon width="5" height="20" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {canViewPlant && (
                <Link to={plant.plantId}>
                  <DropdownMenuItem>Overview Plant</DropdownMenuItem>
                </Link>
              )}

              <DropdownMenuSeparator />

              {canViewControlPanel && (
                <Link to={`${plant.plantId}/control-panel`}>
                  <DropdownMenuItem>Control Panel</DropdownMenuItem>
                </Link>
              )}

              {canViewControlPanel && (
                <Link to={`${plant.plantId}/schedule`}>
                  <DropdownMenuItem>Schedule</DropdownMenuItem>
                </Link>
              )}

              {canViewConnectionDetails && (
                <Link to={`${plant.plantId}/connection-details`}>
                  <DropdownMenuItem>View Connection Details</DropdownMenuItem>
                </Link>
              )}
              <DropdownMenuSeparator />

              {canViewEdit && (
                <Link to={`/edit-plant/${plant.plantId}`}>
                  <DropdownMenuItem>Edit Plant</DropdownMenuItem>
                </Link>
              )}

              {canViewAccessManagement && (
                <PlantUserAccessManagementDialog plant={plant}>
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                    }}
                  >
                    User Access Management
                  </DropdownMenuItem>
                </PlantUserAccessManagementDialog>
              )}

              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled={!allowedUsers.includes(email ?? '')}>
                    <button
                      disabled={!allowedUsers.includes(email ?? '')}
                      className="flex h-full w-full"
                      onClick={() => {
                        deletePlantMutation.mutate({
                          plantId: plant.plantId,
                        });
                      }}
                    >
                      Delete plant
                    </button>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-1 flex w-full justify-between">
          <div className="grid max-w-[400px] grid-cols-2 gap-x-2 gap-y-2 text-sm @md:gap-x-4 @md:gap-y-4">
            {metrics.map((metric, index) => (
              <div key={index} className="flex flex-col border-l-2 py-1 pl-2">
                <span className="text-xs text-[#7D7D85]">{metric.label}</span>
                <div className="flex gap-1 font-medium">
                  <span>{metric.value.value}</span>
                  <span>{metric.value.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});
